import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Transition } from '../../components/Transition';
import { COUNTRY_CODE, LANGUAGE } from '../../constants';
import Iconify from '../../components/Iconify';
import { useState } from 'react';
import { FieldConfig, FieldInputProps } from 'formik';
import UploadAssetsModal from '../../components/UploadAssetsModal';
import { getAssetURl } from '../../utils/url';
import ImageViewer from '../../components/ImageViewer';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  values: any;
  handleSubmit: () => void;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<any>;
  handleChange: (e: React.ChangeEvent<any>) => void;
  errors: any;
  touched: any;
  isSubmitting: boolean;
  resetForm: () => void;
  getFieldProps: (
    nameOrOptions: string | FieldConfig<any>
  ) => FieldInputProps<any>;
}

const AgentFormModal: React.FC<IProps> = ({
  toggle,
  onClose,
  errors,
  touched,
  values,
  isSubmitting,
  setFieldValue,
  handleChange,
  handleSubmit,
  resetForm,
  getFieldProps,
}) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [uploadPhotoModal, setUploadPhotoModal] = useState(false);
  const [uploadBrokerLicenseModal, setUploadBrokerLicenseModal] = useState(false);
  const [uploadIdModal, setUploadIdModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<'image' | 'pdf' | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>('');

  return (
    <>
      <Dialog
        fullWidth
        open={toggle}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => {
          resetForm();
          onClose();
        }}
        aria-describedby='alert-dialog-slide-description'
        maxWidth='md'
      >
        <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>
          {values?.id ? 'Edit Agent' : 'Add Agent'}
        </DialogTitle>
        <form
          onSubmit={handleSubmit}
          autoComplete='off'
        >
          <DialogContent dividers>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  id='name'
                  name='name'
                  label='Name'
                  fullWidth
                  variant='outlined'
                  onChange={handleChange}
                  value={values.name}
                  error={Boolean(errors.name && touched.name)}
                  helperText={Boolean(errors.name && touched.name) && errors.name}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <FormControl
                  sx={{
                    width: { xs: '100%', sm: '100%' },
                  }}
                  error={Boolean(
                    touched?.countryCallingCode && errors?.countryCallingCode
                  )}
                >
                  <InputLabel>{'Country Calling Code'}</InputLabel>
                  <Select
                    name='countryCallingCode'
                    labelId='countryCallingCode'
                    id='countryCallingCode'
                    value={values.countryCallingCode}
                    label={'Country Calling Code'}
                    onChange={(e) =>
                      setFieldValue('countryCallingCode', e.target.value)
                    }
                    MenuProps={{
                      style: {
                        maxHeight: 400,
                      },
                    }}
                  >
                    {COUNTRY_CODE?.map((country) => (
                      <MenuItem
                        key={country.name}
                        value={country.code}
                      >
                        {country.code} {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {Boolean(
                    touched.countryCallingCode && errors.countryCallingCode
                  ) && (
                    <FormHelperText>{errors?.countryCallingCode}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={8}
              >
                <TextField
                  id='mobileNumber'
                  name='mobileNumber'
                  label='Mobile Number'
                  fullWidth
                  variant='outlined'
                  onChange={handleChange}
                  value={values.mobileNumber}
                  error={Boolean(errors.mobileNumber && touched.mobileNumber)}
                  helperText={
                    Boolean(errors.mobileNumber && touched.mobileNumber) &&
                    errors.mobileNumber
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  id='whatsappNumber'
                  name='whatsappNumber'
                  label='WhatsApp Number (Optional)'
                  fullWidth
                  variant='outlined'
                  onChange={handleChange}
                  value={values.whatsappNumber}
                  error={Boolean(errors.whatsappNumber && touched.whatsappNumber)}
                  helperText={
                    Boolean(errors.whatsappNumber && touched.whatsappNumber) &&
                    errors.whatsappNumber
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  id='email'
                  name='email'
                  label={'Email'}
                  fullWidth
                  variant='outlined'
                  onChange={handleChange}
                  value={values.email}
                  error={Boolean(errors.email && touched.email)}
                  helperText={
                    Boolean(errors.email && touched.email) && errors.email
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  fullWidth
                  autoComplete='current-password'
                  type={showPassword ? 'text' : 'password'}
                  label={values?.id ? 'Password (Optional)' : 'Password'}
                  {...getFieldProps('password')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          edge='end'
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          <Iconify
                            icon={
                              showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={Boolean(touched.password && errors.password)}
                  helperText={
                    Boolean(errors.password && touched.password) &&
                    errors.password
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <FormControl
                  fullWidth
                  error={Boolean(touched.language && errors.language)}
                >
                  <InputLabel>Language</InputLabel>
                  <Select
                    id='language'
                    name='language'
                    value={values.language}
                    onChange={(e) => {
                      setFieldValue('language', e.target.value);
                    }}
                    label='Language'
                  >
                    {LANGUAGE.map((lang) => {
                      return (
                        <MenuItem
                          key={lang?.value}
                          value={lang?.value}
                        >
                          {lang?.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {Boolean(touched.language && errors.language) && (
                    <FormHelperText>{errors.language ?? ''}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {values?.id && (
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.activeState}
                        onChange={(e) =>
                          setFieldValue('activeState', e.target.checked)
                        }
                      />
                    }
                    label='Active Status'
                  />
                </Grid>
              )}

              {/* Photo Upload */}
              <Grid
                item
                xs={12}
                sm={12}
              >
                <Button
                  variant='outlined'
                  startIcon={<Iconify icon={'eva:upload-fill'} />}
                  onClick={() => setUploadPhotoModal(true)}
                  fullWidth
                >
                  {values.photoId ? 'Change Photo' : 'Upload Photo'}
                </Button>
                {values.photoId && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Box
                      component='img'
                      src={values.userProfileImage?.key ? getAssetURl(values.userProfileImage.key) : getAssetURl(values.photoId)}
                      alt='Photo'
                      sx={{ maxHeight: 200, maxWidth: 200, objectFit: 'contain' }}
                      onError={(e) => {
                        // Fallback: try using ID as key if direct key doesn't work
                        const target = e.target as HTMLImageElement;
                        if (target.src !== getAssetURl(values.photoId)) {
                          target.src = getAssetURl(values.photoId);
                        }
                      }}
                    />
                  </Box>
                )}
              </Grid>

              {/* Broker License Upload */}
              <Grid
                item
                xs={12}
                sm={6}
              >
                <Button
                  variant='outlined'
                  startIcon={<Iconify icon={'eva:upload-fill'} />}
                  onClick={() => setUploadBrokerLicenseModal(true)}
                  fullWidth
                  color={errors.brokerLicenseId && touched.brokerLicenseId ? 'error' : 'primary'}
                >
                  {values.brokerLicenseId
                    ? 'Change Broker License'
                    : 'Upload Broker License'}
                </Button>
                {errors.brokerLicenseId && touched.brokerLicenseId && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {errors.brokerLicenseId}
                  </FormHelperText>
                )}
                {values.brokerLicenseId && (() => {
                  // Find the broker license attachment to get the key
                  const brokerLicense = values.attachments?.find((att: any) => att.documentType === 'broker_license' && att.id === values.brokerLicenseId);
                  const assetKey = brokerLicense?.key || values.brokerLicenseId;
                  const fileType = brokerLicense?.fileType || '';
                  const isImage = fileType.split('/')[0] === 'image';
                  
                  const handleView = (e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isImage) {
                      setPreviewUrl(getAssetURl(assetKey));
                      setPreviewType('image');
                      setPreviewTitle('Broker License');
                    } else {
                      // For PDFs and documents, open in new tab
                      window.open(getAssetURl(assetKey), '_blank');
                    }
                  };
                  
                  return (
                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                      <Box
                        component='button'
                        type='button'
                        onClick={handleView}
                        sx={{
                          display: 'inline-block',
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        View Broker License
                      </Box>
                    </Box>
                  );
                })()}
              </Grid>

              {/* ID Attachment Upload */}
              <Grid
                item
                xs={12}
                sm={6}
              >
                <Button
                  variant='outlined'
                  startIcon={<Iconify icon={'eva:upload-fill'} />}
                  onClick={() => setUploadIdModal(true)}
                  fullWidth
                  color={errors.idAttachmentId && touched.idAttachmentId ? 'error' : 'primary'}
                >
                  {values.idAttachmentId ? 'Change ID' : 'Upload ID'}
                </Button>
                {errors.idAttachmentId && touched.idAttachmentId && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {errors.idAttachmentId}
                  </FormHelperText>
                )}
                {values.idAttachmentId && (() => {
                  // Find the ID attachment to get the key
                  const idAttachment = values.attachments?.find((att: any) => att.documentType === 'id_attachment' && att.id === values.idAttachmentId);
                  const assetKey = idAttachment?.key || values.idAttachmentId;
                  const fileType = idAttachment?.fileType || '';
                  const isImage = fileType.split('/')[0] === 'image';
                  
                  const handleView = (e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isImage) {
                      setPreviewUrl(getAssetURl(assetKey));
                      setPreviewType('image');
                      setPreviewTitle('ID Document');
                    } else {
                      // For PDFs and documents, open in new tab
                      window.open(getAssetURl(assetKey), '_blank');
                    }
                  };
                  
                  return (
                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                      <Box
                        component='button'
                        type='button'
                        onClick={handleView}
                        sx={{
                          display: 'inline-block',
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        View ID
                      </Box>
                    </Box>
                  );
                })()}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                resetForm();
                onClose();
              }}
            >
              {'Cancel'}
            </Button>
            <LoadingButton
              type='submit'
              variant='contained'
              loading={isSubmitting}
            >
              {values?.id ? 'Update' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Photo Upload Modal */}
      <UploadAssetsModal
        toggle={uploadPhotoModal}
        onClose={() => setUploadPhotoModal(false)}
        setAttachmentArray={(ids: string[]) => {
          if (ids && ids.length > 0) {
            setFieldValue('photoId', ids[0]);
          }
        }}
        module='userprofile'
        moduleId={values?.id || undefined} // Only pass moduleId when editing
        isSingleUpload={true}
      />

      {/* Broker License Upload Modal */}
      <UploadAssetsModal
        toggle={uploadBrokerLicenseModal}
        onClose={() => setUploadBrokerLicenseModal(false)}
        setAttachmentArray={async (ids: string[]) => {
          if (ids && ids.length > 0) {
            await setFieldValue('brokerLicenseId', ids[0], true); // Set value and trigger validation
          }
        }}
        module='broker_license'
        moduleId={values?.id || undefined} // Only pass moduleId when editing
        isSingleUpload={true}
      />

      {/* ID Upload Modal */}
      <UploadAssetsModal
        toggle={uploadIdModal}
        onClose={() => setUploadIdModal(false)}
        setAttachmentArray={async (ids: string[]) => {
          if (ids && ids.length > 0) {
            await setFieldValue('idAttachmentId', ids[0], true); // Set value and trigger validation
          }
        }}
        module='id_attachment'
        moduleId={values?.id || undefined} // Only pass moduleId when editing
        isSingleUpload={true}
      />

      {/* Image Viewer Modal for Images */}
      {previewType === 'image' && previewUrl && (
        <ImageViewer
          open={!!previewUrl}
          onClose={() => {
            setPreviewUrl(null);
            setPreviewType(null);
            setPreviewTitle('');
          }}
          imageUrl={previewUrl}
          alt={previewTitle}
          title={previewTitle}
        />
      )}
    </>
  );
};

export default AgentFormModal;

